import * as React from "react";
import { useLocation } from "wouter";
import { Target, Zap, ChevronRight, ArrowRight, AlertTriangle, Clock, Plus, Sparkles, Truck } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { LoadError } from "@/components/LoadError.js";

interface OrganizingGoalSummary { id: string; title: string; status: string; type?: string }

interface GuidedFlowSummary {
  id: string; title: string; nextDueDate: string; missedCount: number;
  trialStatus: "trial" | "kept" | "discarded"; trialReadyForReview: boolean;
}

/**
 * Organize hub — the section landing. Its ONE job is *status*: "what needs me, and
 * what's underway?" (Needs you now · In progress).
 *
 * Layout (Organize-hub Option C): the hub is a *pure status surface*. The launcher
 * ("Start something" — the 3 category cards) has moved off the hub onto its own
 * surface at `/organize/start`, reached by a persistent primary action in the header
 * (and, on desktop, directly from the left sub-nav). Because the launcher no longer
 * shares the screen, the hub never rearranges — there's nothing to reorder against —
 * so a returning user's status is always in the same place, every visit. The old
 * `isIdle` runtime section reordering is gone (Option A removed it; Option C removes
 * the reason it ever existed).
 *
 * When the user is all caught up, the hub is *allowed to be gloriously empty*: it
 * shows an earned "all caught up" at-rest state (DESIGN.md's "in order / at rest"
 * payoff) plus the same Start-something action — which doubles as first-run
 * activation. "Needs you now" is capped at 3 rows + a "+N more" overflow.
 */
const NEEDS_YOU_LIMIT = 3;

export default function OrganizeHubPage() {
  const [, navigate] = useLocation();
  const [goals, setGoals] = React.useState<OrganizingGoalSummary[]>([]);
  const [flows, setFlows] = React.useState<GuidedFlowSummary[]>([]);
  // Planned moves ("Moves to make") — real pending work, so they count as
  // "needs you" and keep the hub from reading "all caught up" (§6b).
  const [moves, setMoves] = React.useState<{ total: number; staleCount: number }>({ total: 0, staleCount: 0 });
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoadError(false);
    try {
      const [g, f, m] = await Promise.all([
        apiFetch<OrganizingGoalSummary[]>("/api/organizing-goals?status=active"),
        apiFetch<GuidedFlowSummary[]>("/api/guided-flows"),
        // Best-effort: a failure here shouldn't hide the rest of the hub.
        apiFetch<{ total: number; staleCount: number }>("/api/items/planned-moves").catch(() => ({ total: 0, staleCount: 0 })),
      ]);
      setGoals(g); setFlows(f); setMoves({ total: m.total ?? 0, staleCount: m.staleCount ?? 0 });
    } catch {
      // Surface the failure instead of falling through to an empty "create your first
      // goal" state — a down backend must not look like "you have nothing."
      setLoadError(true);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // "Needs you now" = planned moves waiting to be made + trials ready for a
  // keep/discard decision + routines overdue or missed.
  const trialsToReview = flows.filter((f) => f.trialReadyForReview && f.trialStatus === "trial");
  const now = Date.now();
  const overdueFlows = flows.filter((f) => f.trialStatus !== "discarded" && (f.missedCount >= 1 || new Date(f.nextDueDate).getTime() < now));
  // Single, capped attention list (trials first, then overdue).
  const attentionItems: Array<{ kind: "trial" | "overdue"; flow: GuidedFlowSummary }> = [
    ...trialsToReview.map((flow) => ({ kind: "trial" as const, flow })),
    ...overdueFlows.map((flow) => ({ kind: "overdue" as const, flow })),
  ];
  // Pending moves show as ONE row (above the capped list) linking to the
  // grouped "Moves to make" page.
  const hasMoves = moves.total > 0;
  const needsYouCount = attentionItems.length + (hasMoves ? 1 : 0);
  const visibleAttention = attentionItems.slice(0, hasMoves ? NEEDS_YOU_LIMIT - 1 : NEEDS_YOU_LIMIT);
  const hiddenAttentionCount = attentionItems.length - visibleAttention.length;

  const dueRoutines = flows
    .filter((f) => f.trialStatus !== "discarded")
    .sort((a, b) => new Date(a.nextDueDate).getTime() - new Date(b.nextDueDate).getTime());

  // Nothing needs you AND nothing is underway → the earned "at rest" hub.
  const isCaughtUp = needsYouCount === 0 && goals.length === 0 && dueRoutines.length === 0;

  // Persistent launcher entry — the one obvious, always-present door into the
  // launcher surface. Present in the header on every visit so "start something" is
  // never buried and never moves.
  const startAction = (
    <button
      onClick={() => navigate("/organize/start")}
      className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-1 hover:bg-amethyst-ink transition-colors shrink-0"
    >
      <Plus className="w-4 h-4" />Start something
    </button>
  );

  const needsYouSection = needsYouCount > 0 && (
    <section className="space-y-3" key="needs-you">
      <h2 className="text-sm font-semibold text-ink uppercase tracking-wide flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-warning" />Needs you now
      </h2>
      <div className="space-y-2">
        {hasMoves && (
          <button
            key="moves"
            onClick={() => navigate("/organize/moves")}
            className={`w-full text-left flex items-center gap-3 rounded-lg border border-clay-border ${moves.staleCount > 0 ? "bg-warning-wash hover:border-warning/40" : "bg-amethyst-soft hover:border-primary/40"} transition-colors px-4 py-3`}
          >
            <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center shrink-0">
              <Truck className={`w-4 h-4 ${moves.staleCount > 0 ? "text-warning" : "text-primary"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink truncate">Moves to make</div>
              <div className={`text-[0.8125rem] tabular-nums ${moves.staleCount > 0 ? "text-warning-text" : "text-amethyst-ink"}`}>
                {moves.total} thing{moves.total === 1 ? "" : "s"} to move
                {moves.staleCount > 0 && ` · ${moves.staleCount} still to move`}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-faint shrink-0" />
          </button>
        )}
        {visibleAttention.map(({ kind, flow: f }) => (
          kind === "trial" ? (
            <button
              key={`trial-${f.id}`}
              onClick={() => navigate("/organize/routines")}
              className="w-full text-left flex items-center gap-3 rounded-lg border border-clay-border bg-sage-wash hover:border-sage-deep/40 transition-colors px-4 py-3"
            >
              <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center shrink-0"><Zap className="w-4 h-4 text-sage-deep" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink truncate">{f.title}</div>
                <div className="text-[0.8125rem] text-sage-deep">Trial ready — keep it or discard?</div>
              </div>
              <ChevronRight className="w-4 h-4 text-faint shrink-0" />
            </button>
          ) : (
            <button
              key={`overdue-${f.id}`}
              onClick={() => navigate("/organize/routines")}
              className="w-full text-left flex items-center gap-3 rounded-lg border border-clay-border bg-warning-wash hover:border-warning/40 transition-colors px-4 py-3"
            >
              <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center shrink-0"><Clock className="w-4 h-4 text-warning" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink truncate">{f.title}</div>
                <div className="text-[0.8125rem] text-warning-text tabular-nums">
                  {f.missedCount >= 1 ? `Missed ${f.missedCount} time${f.missedCount === 1 ? "" : "s"}` : `Due ${new Date(f.nextDueDate).toLocaleDateString()}`}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-faint shrink-0" />
            </button>
          )
        ))}
        {hiddenAttentionCount > 0 && (
          <button onClick={() => navigate("/organize/routines")} className="text-[0.8125rem] text-faint hover:text-primary px-1 pt-1 tabular-nums">
            +{hiddenAttentionCount} more {hiddenAttentionCount === 1 ? "item needs" : "items need"} attention
          </button>
        )}
      </div>
    </section>
  );

  const inProgressSection = (
    <section className="space-y-3" key="in-progress">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink uppercase tracking-wide">In progress</h2>
        <button
          onClick={() => navigate(goals.length > 0 ? "/organize/goals" : "/organize/goals?new=1")}
          className="text-sm font-medium text-primary flex items-center gap-0.5 hover:underline"
        >
          {goals.length > 0 ? "All goals" : "New goal"}<ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
      {goals.length === 0 && dueRoutines.length === 0 ? (
        <div className="bg-sage-wash rounded-lg border border-clay-border p-6 text-center">
          <p className="text-sm text-body">Nothing in progress yet — use “Start something” above to get going.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {goals.slice(0, 5).map((g) => (
            <button
              key={g.id}
              onClick={() => navigate(`/organize/goals/${g.id}`)}
              className="w-full text-left flex items-center gap-2 rounded-md border border-clay-border hover:border-primary/40 transition-colors px-3 py-2.5 bg-white"
            >
              <Target className="w-4 h-4 text-primary shrink-0" />
              <span className="flex-1 text-sm font-medium text-ink truncate">{g.title}</span>
              <ChevronRight className="w-4 h-4 text-faint shrink-0" />
            </button>
          ))}
          {goals.length > 5 && (
            <button onClick={() => navigate("/organize/goals")} className="text-[0.8125rem] text-faint hover:text-primary px-3 pt-1 tabular-nums">
              +{goals.length - 5} more active {goals.length - 5 === 1 ? "goal" : "goals"}
            </button>
          )}
          {dueRoutines.slice(0, 3).map((f) => (
            <button
              key={`routine-${f.id}`}
              onClick={() => navigate("/organize/routines")}
              className="w-full text-left flex items-center gap-2 rounded-md border border-clay-border hover:border-primary/40 transition-colors px-3 py-2.5 bg-white"
            >
              <Zap className="w-4 h-4 text-primary shrink-0" />
              <span className="flex-1 text-sm font-medium text-ink truncate">{f.title}</span>
              <span className="text-[0.8125rem] text-faint shrink-0 tabular-nums">{new Date(f.nextDueDate).toLocaleDateString()}</span>
              <ChevronRight className="w-4 h-4 text-faint shrink-0" />
            </button>
          ))}
          {dueRoutines.length > 0 && (
            <button onClick={() => navigate("/organize/routines")} className="text-[0.8125rem] text-faint hover:text-primary px-3 pt-1">
              View all routines
            </button>
          )}
        </div>
      )}
    </section>
  );

  if (loading) return <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-28 bg-clay-muted rounded-lg animate-pulse" />)}</div>;

  if (loadError) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold text-ink flex items-center gap-2 font-headline tracking-[-0.01em]"><Target className="w-6 h-6 text-primary" />Organize</h1>
        </div>
        <LoadError onRetry={() => { setLoading(true); load(); }} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header — the status hub's title plus the one persistent door into the
          launcher. "Start something" is always here, so it's never buried by status. */}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-ink flex items-center gap-2 font-headline tracking-[-0.01em]"><Target className="w-6 h-6 text-primary" />Organize</h1>
        {startAction}
      </div>

      {isCaughtUp ? (
        /* The earned "at rest" state — the hub is allowed to be gloriously empty.
           This is also the first-run activation surface: the same Start-something
           action is offered inline so a brand-new user has an obvious next step. */
        <div className="bg-sage-wash rounded-lg border border-clay-border p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-sage-deep" />
          </div>
          <h2 className="text-[1.0625rem] font-semibold text-ink mb-1">All caught up</h2>
          <p className="text-sm text-body max-w-sm mx-auto mb-6">Nothing needs you right now. When you're ready to organize something new, start here.</p>
          <button
            onClick={() => navigate("/organize/start")}
            className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors"
          >
            <Plus className="w-4 h-4" />Start something
          </button>
        </div>
      ) : (
        /* Pure status — always the same regions, same order, every visit. */
        <div className="space-y-6">
          {needsYouSection}
          {inProgressSection}
        </div>
      )}
    </div>
  );
}
